import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import { Button as ShadcnButton } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import DataTable from "@/components/Datatable/Datatable";
import {
  EventItem,
  fetchEventFilterMetadataAction,
} from "@/modules/eventRegistration/features/EventManagement/slices/eventListSlice";
import { useEvents } from "@/modules/eventRegistration/features/EventManagement/hooks/useEvents";
import { createEventColumns } from "@/modules/eventRegistration/features/EventManagement/components/EventColumns/EventColumns";
import EventFilterSidebar from "@/modules/eventRegistration/features/EventManagement/components/EventFilterSidebar/EventFilterSidebar";
import DeleteEventModal from "@/modules/eventRegistration/features/EventManagement/components/DeleteEventModal/DeleteEventModal";
import DuplicateEventModal from "@/modules/eventRegistration/features/EventManagement/components/DuplicateEventModal/DuplicateEventModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { Filter } from "lucide-react";

const EventList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  const { eventTypes, eventCategories, businessRoles, trades, eventTags } =
    useAppSelector((state: RootState) => state.eventList);

  useEffect(() => {
    dispatch(fetchEventFilterMetadataAction());
  }, [dispatch]);

  const {
    events,
    totalCount,
    loading,
    error,
    pagination,
    filters,
    sorting,
    handleFilterChange,
    handlePaginationChange,
    handleSortingChange,
    refetchEvents,
  } = useEvents();

  const handleCreateEvent = useCallback(() => {
    navigate("/admin/events/new");
  }, [navigate]);

  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [eventToDuplicate, setEventToDuplicate] = useState<number | null>(null);

  const openDuplicateModal = useCallback((eventId: number) => {
    setEventToDuplicate(eventId);
    setDuplicateModalOpen(true);
  }, []);

  const closeDuplicateModal = useCallback(() => {
    setDuplicateModalOpen(false);
    setEventToDuplicate(null);
  }, []);

  const handleDuplicateSuccess = useCallback(() => {
    closeDuplicateModal();
    refetchEvents();
  }, [closeDuplicateModal, refetchEvents]);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);

  const handleDeleteEvent = useCallback((id: number) => {
    setEventToDelete(id);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalOpen(false);
    setEventToDelete(null);
  }, []);

  const handleViewSessions = useCallback(
    (uuid: string) => {
      navigate(`/admin/events/${uuid}/sessions`);
    },
    [navigate],
  );

  const handleFiltersChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      handleFilterChange(filterKey, value);
      handlePaginationChange({
        pageIndex: 0,
        pageSize: pagination.pageSize,
      });
    },
    [handleFilterChange, handlePaginationChange, pagination.pageSize],
  );

  const handleEditEvent = useCallback(
    (uuid: string) => {
      navigate(`/admin/events/${uuid}/edit`);
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      createEventColumns({
        onDuplicateEvent: openDuplicateModal,
        onEditEvent: handleEditEvent,
        onDeleteEvent: handleDeleteEvent,
        onViewSessions: handleViewSessions,
      }),
    [
      openDuplicateModal,
      handleEditEvent,
      handleDeleteEvent,
      handleViewSessions,
    ],
  );

  return (
    <>
      <MainPageWrapper
        actions={
          <div className="flex items-center gap-2">
            {isMobile && (
              <ShadcnButton
                onClick={() => setIsFilterSidebarOpen(true)}
                size="sm"
                variant="outline"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </ShadcnButton>
            )}
            <Button onClick={handleCreateEvent}>Create Event</Button>
          </div>
        }
        error={error}
        title="Events Management"
      >
        <div className="flex flex-col md:flex-row gap-6">
          {/* Filter Sidebar */}
          <div
            className={
              isMobile
                ? undefined
                : "w-64 shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 pr-4"
            }
          >
            <EventFilterSidebar
              businessRoles={businessRoles}
              eventCategories={eventCategories}
              eventTags={eventTags}
              eventTypes={eventTypes}
              filters={filters}
              isOpen={isFilterSidebarOpen}
              onFilterChange={handleFiltersChange}
              onOpenChange={setIsFilterSidebarOpen}
              trades={trades}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {totalCount > 0 && `${totalCount} events found`}
            </div>

            <DataTable<EventItem>
              columns={columns}
              data={events}
              error={error}
              loading={loading}
              noDataMessage="No events found"
              onPageChange={(newPageIndex, newPageSize) =>
                handlePaginationChange({
                  pageIndex: newPageIndex,
                  pageSize: newPageSize,
                })
              }
              onSortingChange={handleSortingChange}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              rowKeyExtractor={(item) => item.uuid}
              sorting={sorting}
              totalCount={totalCount}
            />
          </div>
        </div>
      </MainPageWrapper>

      <DuplicateEventModal
        eventId={eventToDuplicate}
        isOpen={isDuplicateModalOpen}
        onClose={closeDuplicateModal}
        onSuccess={handleDuplicateSuccess}
      />

      <DeleteEventModal
        eventId={eventToDelete}
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
      />
    </>
  );
};

export default EventList;
